import environment from '@environment';
import useComponentContext from 'account/recurring-payment/lib/use-component-context';
import { PaymentAttribution } from 'core/enums/payment-type';
import { SitePlatform } from 'core/enums/site-platform';
import useAppContext from 'core/hooks/app-context';
import { PaymentTransactionType, VendorType } from 'core/models/account/make-payment-payload.dto';
import PageRoute from 'core/page-route';
import { PaymentPlatform } from 'core/stores/payment/payment.store';
import { PaymentMetaData } from 'payment/payment.context';
import { RecurringPaymentCardDetails } from '../recurring-payment-component.store';
import { useSetupTranslations, useUpdateTranslations } from '../translations';
import useToCurrency from '../use-to-currency';

const useMakePaymentLogic = (updatePayment: boolean) => {
  const context = useComponentContext();
  const appStore = useAppContext();
  const { authStore, countryCode, paymentStore, locale } = appStore;

  const toCurrency = useToCurrency();

  const countryCodesForIframe = environment.PAYMENT_IFRAME_COUNTRY_CODES;
  const needsPaymentIframe = countryCodesForIframe.includes(countryCode.toLowerCase());

  const update = useUpdateTranslations();
  const setup = useSetupTranslations();

  let url = environment.DSTV_REDIRECT_URL_BASE.replace('{culture}', locale);

  //PayU not accepting localhost urls for ET customers, even though it does for other countries
  //& the api team were unable to sort this out.
  if (url.includes('localhost') && needsPaymentIframe) {
    url = 'https://www.dstv.com/test/#/';
  }

  const hashIndex = url.indexOf('#/');

  const baseUrl = url.substring(0, hashIndex + 1) + PageRoute.account.payment.recurringPayment;

  const nominalAmount = context.amountToPayStore.nominalAmount;

  const paymentIframe = {
    open: () => {
      context.changeShowPaymentIframe(true);
      window.scrollTo({ top: 0, behavior: 'instant' });
    },
    close: () => {
      context.changeShowPaymentIframe(false);
    },
  }

  const redirecting = context.redirecting;

  const getPhoneNumber = () => {
    const { countryCode, number } = context.profileStore.userMobileNumber;

    if (!countryCode || !number) {
      return authStore.user?.customer?.mobileNumber;
    }

    return `+${countryCode}${number}`;
  }

  const makePaymentAsync = (onError: (error: any) => void) => {
    const paymentMeta: PaymentMetaData = {
      paymentAttribution: PaymentAttribution.PayAccount,
      eventData: {},
    };

    const phoneNumber = getPhoneNumber();

    const successReturnUrl = `${baseUrl}/success`;

    //Weird logic which should be handled by api, but is not.
    const backUrl = needsPaymentIframe ? successReturnUrl : baseUrl;

    const request = {
      bypassPaymentSuite: true,
      amount: nominalAmount,
      email: authStore.emailAddress || '',
      recurringPaymentToken: context.cardDetails?.token, //ET & ZM
      encryptedCvv: context.cardDetails?.encryptedcvv, //ET & ZM
      backUrl,
      successReturnUrl,
      failureReturnUrl: `${baseUrl}/failure`,
      consent: 'yes',
      isRecurring: true,
      isRecurringUpdate: updatePayment,
      paymentType: PaymentTransactionType.DstvPayment,
      vendorType: VendorType.DPP,
      invoicePeriod: 1,
      platform: SitePlatform.dstv,
      paymentPlatform: PaymentPlatform.DPP,
      phoneNumber,
      paymentMeta,
      ipDate: context.amountToPayStore.dueDate,// getIpDate(),
    };

    return paymentStore.makePayment(request, false, onError);
  }

  const makePayment = async (onError: (error: any) => void) => {
    if (!needsPaymentIframe) {
      await makePaymentAsync(onError);
      return;
    }

    redirecting.close();
    paymentIframe.open();
  }

  const handlePaymentError = (error: any) => {
    redirecting.close();

    const failure = updatePayment ? update.failure : setup.failure;
    context.showError(failure.text, failure.heading, 'tryAgain');
  }

  const receiveCardDetails = async (event: MessageEvent) => {
    const source = event.data?.source || '';

    if (source.includes('react-devtools')) {
      return;
    }

    let data: RecurringPaymentCardDetails;

    try {
      data = JSON.parse(event.data);
    } catch {
      //React hot reload can trigger this
      data = {
        status: 'back',
      }
    }

    const status = data?.status?.toLowerCase();

    if (status !== 'success') {
      paymentIframe.close();
      return;
    }

    context.changeCardDetails(data);

    paymentIframe.close();
    redirecting.open();

    await makePaymentAsync(handlePaymentError);
  }

  return {
    nominalAmount,
    getFormattedPaymentAmount: () => toCurrency(nominalAmount),
    makePayment,
    receiveCardDetails,
  };
}

export default useMakePaymentLogic;